import { useEffect, useState } from "react";

import { fetchAppInfo, type ProductBranding } from "./api";

/**
 * Брендинг продукта в интерфейсе (D-10): имя инстанса и фиксированный дескриптор.
 *
 * ПОЧЕМУ ИЗ СЕТИ, А НЕ ИЗ СБОРКИ. Клиент собирается ОДИН раз и одним и тем же образом едет во
 * все инстансы (Р-14, docs/05 §8: конфигурация — только через env). Переменная сборки Vite
 * означала бы отдельный образ на каждого заказчика, поэтому имя спрашиваем у сервера в рантайме
 * (`GET /api/app-info` — публичный маршрут: страница входа читает его до аутентификации).
 *
 * ПОЧЕМУ БЕЗ ПОДСТАВНОГО ЗНАЧЕНИЯ. Пока ответ не пришёл (или api недоступен), брендинг не
 * показывается вовсе. Зашить сюда «Облигат» было бы хуже, чем показать пустое место: на инстансе
 * заказчика это мигало бы ЧУЖИМ брендом вместо «АС «КИД»». Значение по умолчанию — забота
 * сервера (`branding/product.ts`), там оно одно на всю систему.
 *
 * Запрос идёт один на загрузку страницы, сколько бы мест его ни показывало: результат и сам
 * незавершённый запрос лежат в модуле.
 */

let cached: ProductBranding | null = null;
let inflight: Promise<ProductBranding | null> | null = null;

/** Сбрасывает кэш модуля. Нужен тестам: иначе соседний тест видит ответ предыдущего. */
export function resetProductBrandingCache(): void {
  cached = null;
  inflight = null;
}

function load(): Promise<ProductBranding | null> {
  inflight ??= fetchAppInfo().then((branding) => {
    cached = branding;
    // Незавершённый запрос забываем: следующий вызов при неудаче попробует снова.
    inflight = null;
    return branding;
  });
  return inflight;
}

export function useProductBranding(): ProductBranding | null {
  const [branding, setBranding] = useState<ProductBranding | null>(cached);

  useEffect(() => {
    if (branding) return;
    let active = true;
    void load().then((result) => {
      if (active && result) setBranding(result);
    });
    return () => {
      active = false;
    };
  }, [branding]);

  // Заголовок вкладки — третье из трёх мест D-10. Ставится здесь, а не в каждом экране:
  // так он один и тот же и на входе, и внутри системы. До ответа сервера остаётся значение
  // из index.html (нейтральное, без чужого бренда).
  useEffect(() => {
    if (branding) document.title = `${branding.name} — ${branding.descriptor}`;
  }, [branding]);

  return branding;
}
